import { useState } from 'react'
import { format } from 'date-fns'
import { es } from 'date-fns/locale'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/components/ui/table'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import { Button } from '@/shared/components/ui/button'
import { Badge } from '@/shared/components/ui/badge'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu'
import { MoreHorizontal, Eye, Trash2, Loader2, User, Mail, Phone, MapPin, Building2, Calendar, Activity } from 'lucide-react'
import type { MedifarmaRegistroListItem } from '../types'
import { useUpdateMedifarmaRegistro, useDeleteMedifarmaRegistro, useMedifarmaOpciones } from '@/modules/medifarma/hooks/useMedifarma'
import { getEstadoBadgeClass } from '../utils/estados'
import { toast } from 'sonner'

interface RegistrosTableProps {
  registros: MedifarmaRegistroListItem[]
  onView?: (id: number) => void
}

export function RegistrosTable({ registros, onView }: RegistrosTableProps) {
  const updateMutation = useUpdateMedifarmaRegistro()
  const deleteMutation = useDeleteMedifarmaRegistro()
  const { data: opciones } = useMedifarmaOpciones()
  const [updatingId, setUpdatingId] = useState<number | null>(null)

  const handleEstadoChange = async (id: number, nuevoEstado: string) => {
    try {
      setUpdatingId(id)
      await updateMutation.mutateAsync({
        id,
        data: { estado: nuevoEstado },
      })
      toast.success('Estado actualizado correctamente')
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || 'Error al actualizar el estado'
      toast.error(errorMessage)
    } finally {
      setUpdatingId(null)
    }
  }

  const handleDelete = async (id: number, nombre: string) => {
    if (!confirm(`¿Está seguro de que desea eliminar el registro de ${nombre}?`)) {
      return
    }

    try {
      await deleteMutation.mutateAsync(id)
      toast.success('Registro eliminado correctamente')
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || 'Error al eliminar el registro'
      toast.error(errorMessage)
    }
  }

  // Todos los estados posibles para el select de cambio de estado
  const estadosDisponibles = opciones?.todos_estados || []

  if (registros.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        No se encontraron registros
      </div>
    )
  }

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader className="bg-gray-100 dark:bg-[hsl(220,15%,16%)] [&_tr]:hover:bg-gray-100 dark:[&_tr]:hover:bg-[hsl(220,15%,16%)]">
          <TableRow>
            <TableHead><div className="flex items-center gap-2"><User className="h-4 w-4" />Nombre</div></TableHead>
            <TableHead><div className="flex items-center gap-2"><Mail className="h-4 w-4" />Correo</div></TableHead>
            <TableHead><div className="flex items-center gap-2"><Phone className="h-4 w-4" />Teléfono</div></TableHead>
            <TableHead><div className="flex items-center gap-2"><MapPin className="h-4 w-4" />Provincia</div></TableHead>
            <TableHead><div className="flex items-center gap-2"><Building2 className="h-4 w-4" />Farmacia</div></TableHead>
            <TableHead><div className="flex items-center gap-2"><Calendar className="h-4 w-4" />Registro</div></TableHead>
            <TableHead className="w-[120px]"><div className="flex items-center gap-2"><Activity className="h-4 w-4" />Estado</div></TableHead>
            <TableHead className="w-[50px] text-right"></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {registros.map((registro) => (
            <TableRow key={registro.id}>
              <TableCell className="font-medium">{registro.nombre}</TableCell>
              <TableCell className="text-sm">{registro.correo}</TableCell>
              <TableCell className="text-sm">{registro.telefono}</TableCell>
              <TableCell>{registro.ciudad}</TableCell>
              <TableCell>{registro.farmacia}</TableCell>
              <TableCell className="text-sm">
                {format(new Date(registro.fecha_registro), 'dd/MM/yyyy HH:mm', {
                  locale: es,
                })}
              </TableCell>
              <TableCell>
                <Select
                  value={registro.estado}
                  onValueChange={(value) => handleEstadoChange(registro.id, value)}
                  disabled={updatingId === registro.id}
                >
                  <SelectTrigger className="w-auto border-none bg-transparent p-0 h-auto" hideChevron>
                    <SelectValue>
                      {updatingId === registro.id ? (
                        <span className="flex items-center gap-2">
                          <Loader2 className="h-3 w-3 animate-spin" />
                          Actualizando...
                        </span>
                      ) : (
                        <Badge className={getEstadoBadgeClass(registro.estado)}>
                          {registro.estado}
                        </Badge>
                      )}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {estadosDisponibles.map((estado) => (
                      <SelectItem key={estado.value} value={estado.value}>
                        <Badge className={getEstadoBadgeClass(estado.value)}>
                          {estado.label}
                        </Badge>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </TableCell>
              <TableCell className="text-right">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-8 w-8">
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {onView && (
                      <DropdownMenuItem onClick={() => onView(registro.id)}>
                        <Eye className="h-4 w-4 mr-2" />
                        Visualizar
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem
                      onClick={() => handleDelete(registro.id, registro.nombre)}
                      disabled={deleteMutation.isPending}
                      className="text-red-600 focus:text-red-600"
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Eliminar
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}
